import React, { useState, useEffect } from "react";
import {
    Layout, Typography, Breadcrumb, Select, Button, Table, Row, Col, Spin, Form, Card, Statistic, Space,
} from "antd";
import {
    SearchOutlined, ClearOutlined, FileExcelOutlined, FilePdfOutlined,
} from "@ant-design/icons";
import axios from "axios";
import { Link } from "react-router-dom";
import { APPBACKGROUND_COLOR, MAINAPIURL } from "../../../appsettings";
import { AlertError } from "../../../usableclasses";
import Headerdiv from "../../../layout/header";
import Footerdiv from "../../../layout/footer";
import { exportStockMovementToExcel } from "./stock-movement-excel";
import { pdf } from "@react-pdf/renderer";
import { saveAs } from "file-saver";
import { StockMovementPdfDocument } from "./stock-movement-pdf";

const { Title } = Typography;
const { Content } = Layout;

// --- Table Columns (No changes here) ---
const grnColumns = [
    { title: "GRN Ref", dataIndex: "grN_REF", key: "grN_REF", width: 120 },
    { title: "Stock", dataIndex: "stocK_NAME", key: "stocK_NAME" },
    { title: "GRN Qty", dataIndex: "grN_QTY", key: "grN_QTY", align: "right" },
    { title: "Cost", dataIndex: "grN_COST", key: "grN_COST", align: "right", render: (val) => val.toFixed(2) },
    { title: "Total", dataIndex: "grN_TOTAL", key: "grN_TOTAL", align: "right", render: (val) => val.toFixed(2) },
];
const transferColumns = [
    { title: "Transfer Ref", dataIndex: "tranS_REF", key: "tranS_REF", width: 120 },
    { title: "From Stock", dataIndex: "fromstock", key: "fromstock" },
    { title: "To Stock", dataIndex: "tostock", key: "tostock" },
    { title: "Transfer Qty", dataIndex: "tranS_QTY", key: "tranS_QTY", align: "right" },
];
const adjustmentColumns = [
    { title: "Date", dataIndex: "cR_DT", key: "cR_DT", width: 180, render: (text) => new Date(text).toLocaleString() },
    { title: "Stock", dataIndex: "stocK_NAME", key: "stocK_NAME" },
    { title: "Type", dataIndex: "adjusT_TYPE", key: "adjusT_TYPE", width: 80 },
    { title: "Adjust Qty", dataIndex: "adjusT_QTY", key: "adjusT_QTY", align: "right" },
    { title: "Note", dataIndex: "adjusT_NOTE", key: "adjusT_NOTE", render: (text) => <div dangerouslySetInnerHTML={{ __html: text }} /> },
];
const orderColumns = [
    { title: "Order Ref", dataIndex: "ordeR_REF", key: "ordeR_REF", width: 150 },
    { title: "Order Qty", dataIndex: "ordeR_QTY", key: "ordeR_QTY", align: "right" },
    { title: "Subtotal", dataIndex: "ordeR_SUBTOTAL", key: "ordeR_SUBTOTAL", align: "right", render: (val) => val.toFixed(2) },
];

// --- Filters (No changes here) ---
const getYears = () => {
    const currentYear = new Date().getFullYear();
    return Array.from({ length: 6 }, (_, i) => currentYear - i);
};
const months = [
    { value: 1, label: "January" }, { value: 2, label: "February" }, { value: 3, label: "March" },
    { value: 4, label: "April" }, { value: 5, label: "May" }, { value: 6, label: "June" },
    { value: 7, label: "July" }, { value: 8, label: "August" }, { value: 9, label: "September" },
    { value: 10, label: "October" }, { value: 11, label: "November" }, { value: 12, label: "December" },
];

const StockItemMovementReport = () => {
    const [form] = Form.useForm();
    const ApiURL = MAINAPIURL;
    const TOKEN = localStorage.getItem("AUTHCODE");

    const [isLoading, setIsLoading] = useState(false);
    const [products, setProducts] = useState([]);
    const initialDataState = {
        grnList: [], adjustList: [], transList: [], orderList: [],
        sumGrnQty: 0, sumAdjustAddQty: 0, sumAdjustMinQty: 0, sumTransQty: 0, sumOrderQty: 0,
    };
    const [reportData, setReportData] = useState(initialDataState);
    const isDataAvailable =
        reportData.grnList.length > 0 ||
        reportData.transList.length > 0 ||
        reportData.adjustList.length > 0 ||
        reportData.orderList.length > 0;

    // --- Data Fetching (No changes here) ---
    useEffect(() => {
        fetchProducts();
    }, [ApiURL]);

    const fetchProducts = async () => {
        try {
            const headers = { SystemAccessToken: TOKEN };
            const res = await axios.get(`${ApiURL}/products/dropdown/all`, { headers });
            setProducts(res.data);
        } catch (error) {
            AlertError("Failed to load product list.");
        }
    };

    const fetchStockMovementDetails = async (values) => {
        const { year, month, productId } = values;
        if (!productId) return AlertError("Please select a product.");

        try {
            setIsLoading(true);
            const headers = { SystemAccessToken: TOKEN };
            const formattedMonth = month.toString().padStart(2, "0");
            const res = await axios.get(
                `${ApiURL}/reports/stock/stockmovementbyproduct/${year}/${formattedMonth}/${productId}`,
                { headers }
            );
            setReportData(res.data);
        } catch (error) {
            AlertError("Failed to fetch stock movement details.");
            setReportData(initialDataState);
        } finally {
            setIsLoading(false);
        }
    };

    // --- Export Handlers (No changes here) ---
    const handleExportClick = () => {
        setIsLoading(true);
        try {
            const filters = form.getFieldsValue();
            const success = exportStockMovementToExcel(reportData, filters, products);
            if (!success) AlertError("No data available to export.");
        } catch {
            AlertError("Error while creating Excel file.");
        } finally {
            setIsLoading(false);
        }
    };

    const handlePdfExport = async () => {
        if (!isDataAvailable) return AlertError("No data available to export.");
        setIsLoading(true);
        try {
            const filters = form.getFieldsValue();
            const selectedProduct = products.find((p) => p.id === filters.productId);
            const productName = selectedProduct ? selectedProduct.label : "N/A";

            // Consistent filename formatting
            const formattedMonth = String(filters.month).padStart(2, "0");
            const fileName = `Stock_Movement_${productName.replace(/\s+/g, "_")}_${filters.year}-${formattedMonth}.pdf`;

            const doc = <StockMovementPdfDocument data={reportData} filters={filters} productName={productName} />;
            const blob = await pdf(doc).toBlob();
            saveAs(blob, fileName);
        } catch (error) {
            console.error("PDF Export Error:", error);
            AlertError("Error while creating PDF file.");
        } finally {
            setIsLoading(false);
        }
    };

    const handleClear = () => {
        form.resetFields();
        form.setFieldsValue({
            year: new Date().getFullYear(),
            month: new Date().getMonth() + 1,
        });
        setReportData(initialDataState);
    };

    return (
        <Spin spinning={isLoading} tip="Fetching Data...">
            <Layout style={{ backgroundColor: APPBACKGROUND_COLOR, minHeight: "100vh" }}>
                <Headerdiv />
                <Content style={{ padding: "1rem" }}>
                    <Breadcrumb style={{ margin: "16px 0" }}>
                        <Breadcrumb.Item><Link to="/reports">Reports</Link></Breadcrumb.Item>
                        <Breadcrumb.Item>Stock Item Movement</Breadcrumb.Item>
                    </Breadcrumb>
                    <Card
                        style={{ marginBottom: 20 }}
                        title={
                            <Row justify="space-between" align="middle">
                                <Col>
                                    <Title level={4} style={{ margin: 0 }}>Stock Item Movement Report</Title>
                                </Col>
                                <Col>
                                    <Space>
                                        <Button type="primary"
                                            icon={<FileExcelOutlined />}
                                            onClick={handleExportClick}
                                            style={{ backgroundColor: "#28a745", borderColor: "#28a745" }}
                                            disabled={!isDataAvailable}>Export to Excel</Button>
                                        <Button type="primary"
                                            icon={<FilePdfOutlined />}
                                            onClick={handlePdfExport}
                                            style={{ backgroundColor: "#dc3545", borderColor: "#dc3545" }}
                                            disabled={!isDataAvailable}>Export to PDF</Button>
                                    </Space>
                                </Col>
                            </Row>
                        }
                    >
                        <Form form={form} layout="vertical" onFinish={fetchStockMovementDetails} initialValues={{ year: new Date().getFullYear(), month: new Date().getMonth() + 1, }}>
                            <Row gutter={[16, 16]} align="bottom">
                                <Col xs={24} sm={12} md={8} lg={6}>
                                    <Form.Item label="Product" name="productId" rules={[{ required: true, message: "Please select a product!" }]}>
                                        <Select placeholder="Select a Product" options={products.map((p) => ({ value: p.id, label: p.label }))} allowClear />
                                    </Form.Item>
                                </Col>
                                <Col xs={12} sm={6} md={4} lg={3}>
                                    <Form.Item label="Year" name="year">
                                        <Select options={getYears().map((y) => ({ value: y, label: y }))} />
                                    </Form.Item>
                                </Col>
                                <Col xs={12} sm={6} md={4} lg={3}>
                                    <Form.Item label="Month" name="month">
                                        <Select options={months} />
                                    </Form.Item>
                                </Col>
                                <Col xs={24} sm={12} md={8} lg={8}>
                                    <Form.Item>
                                        <Space wrap>
                                            <Button htmlType="submit" type="primary" icon={<SearchOutlined />}>Get Data</Button>
                                            <Button icon={<ClearOutlined />} onClick={handleClear}>Clear</Button>
                                        </Space>
                                    </Form.Item>
                                </Col>
                            </Row>
                        </Form>
                    </Card>
                    <Row gutter={[16, 16]} justify="space-between" style={{ marginBottom: 20 }}>
                        {[
                            { title: "Total GRN Qty", value: reportData.sumGrnQty },
                            { title: "Total Transfer Qty", value: reportData.sumTransQty },
                            { title: "Adjustment (Add)", value: reportData.sumAdjustAddQty },
                            { title: "Adjustment (Minus)", value: reportData.sumAdjustMinQty },
                            { title: "Total Order Qty", value: reportData.sumOrderQty },
                        ].map((item, i) => (
                            <Col key={i} xs={12} sm={8} md={6} lg={4} xl={4}>
                                <Card className="h-full">
                                    <Statistic title={item.title} value={item.value} />
                                </Card>
                            </Col>
                        ))}
                    </Row>
                    <Space direction="vertical" size="large" style={{ width: "100%" }}>
                        <Card title="GRN Details Summary">
                            <Table columns={grnColumns} dataSource={reportData.grnList} bordered size="small" scroll={{ x: true }} rowKey="grN_REF" pagination={{ pageSize: 20 }} />
                        </Card>
                        <Card title="Transfer Details Summary">
                            <Table columns={transferColumns} dataSource={reportData.transList} bordered size="small" scroll={{ x: true }} rowKey="tranS_REF" pagination={{ pageSize: 10 }} />
                        </Card>
                        <Card title="Adjustment Details Summary">
                            <Table columns={adjustmentColumns} dataSource={reportData.adjustList} bordered size="small" scroll={{ x: true }} rowKey={(r, i) => `${r.cR_DT}-${i}`} pagination={{ pageSize: 10 }} />
                        </Card>
                        <Card title="Order Details Summary">
                            <Table columns={orderColumns} dataSource={reportData.orderList} bordered size="small" scroll={{ x: true }} rowKey="ordeR_REF" pagination={{ pageSize: 10 }} />
                        </Card>
                    </Space>
                </Content>
                <Footerdiv />
            </Layout>
        </Spin>
    );
};

export default StockItemMovementReport;